import { useState, useMemo } from "react";
import useApi from "../../hooks/useApi";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

export default function Review() {
    const { get, del } = useApi();
    const { data, isLoading, isError, error } = get("/allreview");

    const reviews = data?.data || [];

    const [expandedVendor, setExpandedVendor] = useState(null);
    const [search, setSearch] = useState("");

    const toggleVendor = (vendorId) => {
        setExpandedVendor(prev => (prev === vendorId ? null : vendorId));
    };

    /* ⭐ Render Stars */
    const renderStars = (rating) => (
        <ul className="rating d-flex mb-0">
            {[1, 2, 3, 4, 5].map((star) => (
                <li key={star}>
                    <i className={`fas fa-star ${star <= rating ? "theme-color" : ""}`}></i>
                </li>
            ))}
        </ul>
    );

    /* 🔍 Search + Group Reviews by Vendor */
    const groupedReviews = useMemo(() => {
        const keyword = search.toLowerCase();

        const filtered = reviews.filter((review) =>
            review.comment?.toLowerCase().includes(keyword) ||
            review.customer_name?.toLowerCase().includes(keyword) ||
            review.vendor_name?.toLowerCase().includes(keyword)
        );

        return filtered.reduce((acc, review) => {
            const vendorKey = review.vendor_id;

            if (!acc[vendorKey]) {
                acc[vendorKey] = {
                    vendor_name: review.vendor_name,
                    reviews: []
                };
            }

            acc[vendorKey].reviews.push(review);
            return acc;
        }, {});
    }, [reviews, search]);

    /* 🗑️ Delete Handler with SweetAlert & React Query */
    const deleteMutation = del("/review", {
        onSuccess: () => {
            toast.success("Review deleted successfully!");
        },
        onError: () => {
            toast.error("Failed to delete review.");
        }
    });

    const handleDelete = (review) => {
        Swal.fire({
            title: `Delete review #${review.review_id}?`,
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then((result) => {
            if (result.isConfirmed) {
                deleteMutation.mutate(review.review_id);
            }
        });
    };

    return (
        <>
            {/* 🔹 Header */}
            <div className="title-header option-title d-flex justify-content-between align-items-center">
                <h2>Vendor Reviews</h2>
                <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: 280 }}
                    placeholder="Search vendor / Review / customer..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* 🔄 Loader */}
            {isLoading && (
                <div className="text-center my-5">
                    <div className="spinner-border" role="status"></div>
                </div>
            )}

            {/* ❌ Error */}
            {isError && (
                <div className="text-center text-danger my-4">
                    {error?.response?.data?.message || "Error loading reviews"}
                </div>
            )}

            {/* 📦 No Reviews */}
            {!isLoading && Object.keys(groupedReviews).length === 0 && (
                <div className="text-center text-muted my-4">
                    No reviews found
                </div>
            )}

            {/* 📦 Render Vendors & Reviews */}
            {Object.entries(groupedReviews).map(([vendorId, vendorData], index) => (
                <div
                    key={vendorId}
                    className="mb-4 border rounded"
                    style={{ background: "#fff" }}
                >
                    {/* 🔹 Vendor Header */}
                    <div
                        onClick={() => toggleVendor(vendorId)}
                        style={{
                            cursor: "pointer",
                            padding: "14px 18px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "#f8f9fa",
                            color: "#000",
                            borderBottom: "1px solid #e5e5e5"
                        }}
                    >
                        <div style={{ fontWeight: 600 }}>
                            {index + 1}. {vendorData.vendor_name || "Vendor"}
                            <span
                                style={{
                                    marginLeft: 8,
                                    fontSize: 13,
                                    color: "#666"
                                }}
                            >
                                ({vendorData.reviews.length} reviews)
                            </span>
                        </div>

                        <i
                            className={`ri-arrow-${expandedVendor === vendorId ? "up" : "down"}-s-line`}
                            style={{ fontSize: 22, color: "#333" }}
                        ></i>
                    </div>

                    {/* 🔽 Expand Table */}
                    {expandedVendor === vendorId && (
                        <div style={{ background: "#fff", padding: 16 }}>
                            <div className="table-responsive">
                                <table className="table theme-table">
                                    <thead>
                                        <tr>
                                            <th>No.</th>
                                            <th>Customer</th>
                                            <th>Rating</th>
                                            <th>Review</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {vendorData.reviews.map((review, i) => (
                                            <tr key={review.review_id}>
                                                <td>{i + 1}</td>
                                                <td>{review.customer_name || `Customer #${review.customer_id}`}</td>
                                                <td>{renderStars(review.rating)}</td>
                                                <td>{review.comment}</td>
                                                <td>
                                                    <i
                                                        className="ri-delete-bin-line text-danger"
                                                        style={{ cursor: "pointer", fontSize: 18 }}
                                                        onClick={() => handleDelete(review)}
                                                    ></i>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </>
    );
}
